import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Expand,
    Copy,
    ExternalLink,
    AlertTriangle,
    Pin,
    Package,
    Info,
    FolderPlus,
    ArrowRight,
    ArrowLeft,
    Search,
    Flag,
    Layers,
    List,
    Hash,
    Combine,
} from 'lucide-react';
import { safeStr } from '../utils/safeStr';
import { useAnchoredMenu, WORKBENCH_MENU_TOP_INSET } from '../hooks/useAnchoredMenu';

interface NodeContextMenuProps {
    node: {
        id: string;
        label?: string;
        entity_name?: string;
        type?: string;
        /** Starburst-cluster fields — present when node.type === 'cluster' */
        tx_hash?: string;
        cospender_count?: number;
        cluster_kind?: 'in' | 'out';
    };
    position: { x: number; y: number };
    chain?: string;
    /** True when this non-cluster node has at least one starburst cluster
     *  bundled against it (i.e. it's the convergence of bundled co-spenders). */
    hasBundledClusters?: boolean;
    onClose: () => void;
    onExpand: () => void;
    onViewDetails: () => void;
    onPin?: () => void;
    onHide?: () => void;
    onMarkSuspicious?: () => void;
    onAddToCase?: () => void;
    /** R-CANVAS-22: Trace forward from this node */
    onTraceForward?: () => void;
    /** R-CANVAS-22: Trace backward from this node */
    onTraceBackward?: () => void;
    /** R-CANVAS-22 / P1-D3: Re-run Auto-Investigate for this node (sequential SSE) */
    onInvestigate?: () => void;
    /** R-CANVAS-22: Flag this node for review */
    onFlag?: () => void;
    /** Cluster-only: open Transfers tab on the convergence node, auto-expand
     *  the matching tx_hash group inline. Default safe action — no canvas
     *  mutation, no layout reflow. */
    onViewClusterInTransfers?: () => void;
    /** Cluster-only: replace the cluster placeholder with its bundled
     *  co-spenders + edges (the old left-click behavior, now opt-in). */
    onExpandClusterOnCanvas?: () => void;
    /** Cluster-only: copy the underlying transaction hash to clipboard. */
    onCopyClusterTxHash?: () => void;
    /** Convergence-node-only: re-run bundleStarburstEdges over current
     *  elements to fold any expanded clusters back into compact bundles. */
    onRebundleStarbursts?: () => void;
    /** Multi-node trace: Triggered when multiple nodes are selected */
    onHolisticTrace?: () => void;
    /** Current selected node IDs to determine if multi-trace is applicable */
    selectedNodeIds?: string[];
}

const getExplorerUrl = (address: string, chain?: string): string => {
    const c = (chain || '').toUpperCase();
    if (c === 'LTC' || address.startsWith('ltc1') || ((address.startsWith('L') || address.startsWith('M')) && address.length === 34)) {
        return `https://litecoinspace.org/address/${address}`;
    }
    if (c === 'DOGE' || ((address.startsWith('D') || address.startsWith('A') || address.startsWith('9')) && address.length === 34)) {
        return `https://blockchair.com/dogecoin/address/${address}`;
    }
    if (c === 'BCH') {
        return `https://blockchair.com/bitcoin-cash/address/${address}`;
    }
    if (address.startsWith('T')) {
        return `https://tronscan.org/#/address/${address}`;
    }
    if (address.startsWith('0x')) {
        if (chain === 'BNB') return `https://bscscan.com/address/${address}`;
        if (chain === 'OPBNB') return `https://opbnb.bscscan.com/address/${address}`;
        if (chain === 'MATIC') return `https://polygonscan.com/address/${address}`;
        return `https://etherscan.io/address/${address}`;
    }
    if (address.startsWith('1') || address.startsWith('3') || address.startsWith('bc1')) {
        return `https://blockstream.info/address/${address}`;
    }
    return '#';
};

export const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
    node,
    position,
    chain,
    hasBundledClusters,
    onClose,
    onExpand,
    onViewDetails,
    onPin,
    onHide,
    onMarkSuspicious,
    onAddToCase,
    onTraceForward,
    onTraceBackward,
    onInvestigate,
    onFlag,
    onViewClusterInTransfers,
    onExpandClusterOnCanvas,
    onCopyClusterTxHash,
    onRebundleStarbursts,
    onHolisticTrace,
    selectedNodeIds = [],
}) => {
    const isCluster = node.type === 'cluster';
    const cospCount = node.cospender_count ?? 0;
    const kindLabel = node.cluster_kind === 'in' ? 'co-inputs' : node.cluster_kind === 'out' ? 'co-outputs' : 'cospenders';
    const isMultiSelect = selectedNodeIds.length > 1 && selectedNodeIds.includes(node.id);

    // Measured placement — the menu's height depends on which entry groups are
    // shown (cluster / convergence / multi-select each render a different list),
    // so it cannot be predicted from a constant. Top inset clears the Workbench
    // header so a top-half right-click never hides items under Trace/Investigate.
    const { ref: menuRef, style: anchoredStyle } = useAnchoredMenu<HTMLDivElement>(position, {
        insets: { top: WORKBENCH_MENU_TOP_INSET },
    });
    const menuStyle: React.CSSProperties = { ...anchoredStyle, zIndex: 9999 };

    // Escape still closes; outside clicks / right-clicks are owned by the
    // fullscreen backdrop below (same pattern as Fusion RadialMenu) so the
    // browser OS menu cannot appear beside this one.
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        // While open, block the OS menu anywhere — the opening click is handled
        // on GraphCanvas, but a second right-click on chrome/inspector must not
        // stack the browser menu over ours.
        const blockOsMenu = (event: Event) => {
            event.preventDefault();
        };

        document.addEventListener('keydown', handleEscape);
        document.addEventListener('contextmenu', blockOsMenu, true);
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('contextmenu', blockOsMenu, true);
        };
    }, [onClose]);

    const copyAddress = () => {
        navigator.clipboard.writeText(node.id);
        onClose();
    };

    const openExplorer = () => {
        window.open(getExplorerUrl(node.id, chain), '_blank');
        onClose();
    };

    const handleExpand = () => {
        onExpand();
        onClose();
    };

    const handleViewDetails = () => {
        onViewDetails();
        onClose();
    };

    const menuItems = [
        // ── Multi-Node Entries ──
        {
            icon: Combine,
            label: `Holistic Trace (${selectedNodeIds.length} nodes)`,
            onClick: () => { onHolisticTrace?.(); onClose(); },
            color: 'text-primary',
            show: !isCluster && isMultiSelect && !!onHolisticTrace,
        },
        { divider: true, show: !isCluster && isMultiSelect },
        // ── Cluster-only entries (replace the trace/investigate block when
        //    the right-clicked node is a starburst-cluster placeholder) ──
        {
            icon: List,
            label: cospCount > 0 ? `View ${cospCount} ${kindLabel} in Transfers` : `View ${kindLabel} in Transfers`,
            onClick: () => { onViewClusterInTransfers?.(); onClose(); },
            color: 'text-primary',
            show: isCluster && !!onViewClusterInTransfers,
        },
        {
            icon: Layers,
            label: cospCount > 0 ? `Expand ${cospCount} on canvas` : 'Expand on canvas',
            onClick: () => { onExpandClusterOnCanvas?.(); onClose(); },
            color: 'text-blue-400',
            show: isCluster && !!onExpandClusterOnCanvas,
        },
        {
            icon: Hash,
            label: 'Copy tx hash',
            onClick: () => { onCopyClusterTxHash?.(); onClose(); },
            color: 'text-foreground/80',
            show: isCluster && !!onCopyClusterTxHash && !!node.tx_hash,
        },
        // ── Convergence-node entries (non-cluster nodes that have at least
        //    one bundled cluster attached) ──
        {
            icon: Combine,
            label: 'Re-bundle expanded co-spenders',
            onClick: () => { onRebundleStarbursts?.(); onClose(); },
            color: 'text-slate-300',
            show: !isCluster && !!hasBundledClusters && !!onRebundleStarbursts,
        },
        { divider: true, show: isCluster || !!hasBundledClusters },
        // ── Default entries (suppressed for cluster placeholders since
        //    expand/trace/investigate don't make sense on a synthetic node) ──
        {
            icon: Expand,
            label: 'Expand Connections',
            onClick: handleExpand,
            color: 'text-blue-400',
            show: !isCluster,
        },
        {
            icon: ArrowRight,
            label: 'Trace Forward',
            onClick: () => { onTraceForward?.(); onClose(); },
            color: 'text-emerald-400',
            show: !isCluster && !!onTraceForward,
        },
        {
            icon: ArrowLeft,
            label: 'Trace Backward',
            onClick: () => { onTraceBackward?.(); onClose(); },
            color: 'text-amber-400',
            show: !isCluster && !!onTraceBackward,
        },
        {
            icon: Search,
            label: 'Re-run Auto-Investigate',
            onClick: () => { onInvestigate?.(); onClose(); },
            color: 'text-purple-400',
            show: !isCluster && !!onInvestigate,
        },
        { divider: true, show: !isCluster },
        {
            icon: Copy,
            label: 'Copy Address',
            onClick: copyAddress,
            color: 'text-foreground/80',
            show: !isCluster,
        },
        {
            icon: ExternalLink,
            label: 'View on Explorer',
            onClick: openExplorer,
            color: 'text-foreground/80',
            show: !isCluster,
        },
        { divider: true, show: !isCluster },
        {
            icon: Flag,
            label: 'Flag for Review',
            onClick: () => { onFlag?.(); onClose(); },
            color: 'text-red-400',
            show: !isCluster && !!onFlag,
        },
        {
            icon: AlertTriangle,
            label: 'Mark Suspicious',
            onClick: () => { onMarkSuspicious?.(); onClose(); },
            color: 'text-orange-400',
            show: !isCluster && !!onMarkSuspicious,
        },
        {
            icon: FolderPlus,
            label: 'Add to Case',
            onClick: () => { onAddToCase?.(); onClose(); },
            color: 'text-primary',
            show: !isCluster && !!onAddToCase,
        },
        { divider: true, show: !isCluster },
        {
            icon: Pin,
            label: 'Pin Position',
            onClick: () => { onPin?.(); onClose(); },
            color: 'text-foreground/80',
            show: !isCluster && !!onPin,
        },
        {
            icon: Package,
            label: 'Stash Node',
            onClick: () => { onHide?.(); onClose(); },
            color: 'text-amber-400',
            show: !isCluster && !!onHide,
        },
        { divider: true, show: !isCluster },
        {
            icon: Info,
            label: 'View Details',
            onClick: handleViewDetails,
            color: 'text-foreground/80',
            show: !isCluster,
        },
    ];

    return createPortal(
        <>
        {/* Capture right-clicks outside the panel so the OS menu never stacks
            on top of ours; left-click dismisses (Fusion RadialMenu pattern). */}
        <div
            className="fixed inset-0 z-[9998]"
            data-testid="node-context-menu-backdrop"
            onClick={onClose}
            onContextMenu={(e) => {
                e.preventDefault();
                // Do NOT onClose() here. Cytoscape fires cxttap on mouseup, React
                // mounts this backdrop, then the same gesture's contextmenu hits
                // the backdrop and would instantly dismiss the menu we just opened.
                // Left-click (onClick) still dismisses; Escape still works.
            }}
        />
        <div
            ref={menuRef}
            style={menuStyle}
            data-testid="node-context-menu"
            onContextMenu={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
            // Do not use overflow-hidden here — when the viewport is short,
            // useAnchoredMenu sets overflowY:auto + maxHeight so officers can
            // scroll every action. Header uses its own rounded clip below.
            className="bg-card/95 backdrop-blur-sm border border-border/80 rounded-lg shadow-2xl py-1 min-w-[200px] styled-scrollbar"
        >
            {/* Header */}
            <div className="px-3 py-2 border-b border-border/50 bg-muted/50 rounded-t-lg">
                <div className="text-xs text-muted-foreground truncate">
                    {safeStr(node.entity_name) || safeStr(node.label) || 'Node'}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono truncate">
                    {String(node.id ?? '').slice(0, 20)}{String(node.id ?? '').length > 20 ? '...' : ''}
                </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
                {menuItems.map((item, index) => {
                    if (item.divider) {
                        if (item.show === false) return null;
                        return <div key={index} className="my-1 border-t border-border/50" />;
                    }

                    if (item.show === false || !item.icon) {
                        return null;
                    }

                    const IconComponent = item.icon;
                    return (
                        <button
                            key={index}
                            onClick={item.onClick}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                        >
                            <IconComponent className={`w-4 h-4 ${item.color}`} />
                            <span>{item.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
        </>,
        document.body,
    );
};

export default NodeContextMenu;
